import { util as sdkUtil } from '../util/sdkLoader';
import { denormalisedResponseEntities, ensureOwnListing } from '../util/data';
import * as log from '../util/log';
import { LISTING_STATE_DRAFT } from '../util/types';
import { storableError } from '../util/errors';
import { isUserAuthorized } from '../util/userHelpers';
import {
  getTransitionsNeedingCustomerAttention,
  getTransitionsNeedingProviderAttention,
} from '../transactions/transaction';
import { getStreamChatNotificationsApi } from '../util/api';
import {
  createNormalizedMessageNotificationMap,
  getUnreadMessageNotificationTxIds,
} from '../util/notificationHelpers';
import {
  getResolvedActiveRoleUpdatePayload,
  shouldPersistResolvedActiveRole,
} from '../util/activeRole';

import { authInfo } from './auth.duck';
import { stripeAccountCreateSuccess } from './stripeConnectAccount.duck';
import { retrieveOmiseRecipient } from '../containers/StripePayoutPage/StripePayoutPage.duck';

// ================ Action types ================ //

export const CURRENT_USER_SHOW_REQUEST = 'app/user/CURRENT_USER_SHOW_REQUEST';
export const CURRENT_USER_SHOW_SUCCESS = 'app/user/CURRENT_USER_SHOW_SUCCESS';
export const CURRENT_USER_SHOW_ERROR = 'app/user/CURRENT_USER_SHOW_ERROR';

export const CLEAR_CURRENT_USER = 'app/user/CLEAR_CURRENT_USER';

export const FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST';
export const FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_LISTINGS_ERROR =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_ERROR';

export const FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST';
export const FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS';
export const FETCH_CURRENT_USER_NOTIFICATIONS_ERROR =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_ERROR';

export const FETCH_CURRENT_USER_HAS_ORDERS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_REQUEST';
export const FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_ORDERS_ERROR = 'app/user/FETCH_CURRENT_USER_HAS_ORDERS_ERROR';

export const SEND_VERIFICATION_EMAIL_REQUEST = 'app/user/SEND_VERIFICATION_EMAIL_REQUEST';
export const SEND_VERIFICATION_EMAIL_SUCCESS = 'app/user/SEND_VERIFICATION_EMAIL_SUCCESS';
export const SEND_VERIFICATION_EMAIL_ERROR = 'app/user/SEND_VERIFICATION_EMAIL_ERROR';

// ================ Reducer ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
    ? newCurrentUser
    : { id, type, attributes, ...oldRelationships, ...relationships };
};

const initialState = {
  currentUser: null,
  currentUserShowTimestamp: 0,
  currentUserShowError: null,
  currentUserHasListings: false,
  currentUserHasListingsError: null,
  currentUserAttentionTransactions: [],
  currentUserAttentionTxIds: [],
  currentUserMessageNotifications: {},
  currentUserUnreadMessageTxIds: [],
  currentUserNotificationCount: 0,
  currentUserNotificationCountError: null,
  currentUserHasOrders: null, // This is not fetched unless unverified emails exist
  currentUserHasOrdersError: null,
  sendVerificationEmailInProgress: false,
  sendVerificationEmailError: null,
};
let fetchCurrentUserNotificationsRequestPromise = null;
let fetchCurrentUserNotificationsRequestMode = null;
let userSessionEpoch = 0;

const FETCH_CURRENT_USER_NOTIFICATIONS_MODE = {
  ATTENTION_ONLY: 'attention-only',
  WITH_MESSAGES: 'with-messages',
};

const persistResolvedActiveRoleIfNeeded = (currentUser, sdk, dispatch) => {
  if (!shouldPersistResolvedActiveRole(currentUser)) {
    return Promise.resolve(currentUser);
  }

  if (typeof sdk?.currentUser?.updateProfile !== 'function') {
    return Promise.resolve(currentUser);
  }

  const params = getResolvedActiveRoleUpdatePayload(currentUser);
  const queryParams = {
    expand: true,
    include: ['profileImage', 'stripeAccount'],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-xsmall',
      'variants.square-xsmall2x',
    ],
  };

  return sdk.currentUser
    .updateProfile(params, queryParams)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }

      const updatedCurrentUser = entities[0];
      dispatch(currentUserShowSuccess(updatedCurrentUser));
      return updatedCurrentUser;
    })
    .catch(e => {
      console.error(e); // eslint-disable-line
      return currentUser;
    });
};

export const resetUserSessionState = () => {
  userSessionEpoch += 1;
  fetchCurrentUserNotificationsRequestPromise = null;
  fetchCurrentUserNotificationsRequestMode = null;
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CURRENT_USER_SHOW_REQUEST:
      return { ...state, currentUserShowError: null };
    case CURRENT_USER_SHOW_SUCCESS:
      return {
        ...state,
        currentUser: mergeCurrentUser(state.currentUser, payload),
        currentUserShowTimestamp: payload ? new Date().getTime() : 0,
      };
    case CURRENT_USER_SHOW_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, currentUserShowError: payload };

    case CLEAR_CURRENT_USER:
      return {
        ...state,
        currentUser: null,
        currentUserShowTimestamp: 0,
        currentUserShowError: null,
        currentUserHasListings: false,
        currentUserHasListingsError: null,
        currentUserAttentionTransactions: [],
        currentUserAttentionTxIds: [],
        currentUserMessageNotifications: {},
        currentUserUnreadMessageTxIds: [],
        currentUserNotificationCount: 0,
        currentUserNotificationCountError: null,
        currentUserHasOrders: null,
        currentUserHasOrdersError: null,
        sendVerificationEmailInProgress: false,
        sendVerificationEmailError: null,
      };

    case FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST:
      return { ...state, currentUserHasListingsError: null };
    case FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS:
      return { ...state, currentUserHasListings: payload.hasListings };
    case FETCH_CURRENT_USER_HAS_LISTINGS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasListingsError: payload };

    case FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST:
      return { ...state, currentUserNotificationCountError: null };
    case FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS: {
      const hasMessageNotificationPayload =
        Array.isArray(payload.messageNotifications) ||
        Array.isArray(payload.unreadMessageNotifications);
      const attentionTransactions = payload.attentionTransactions || [];
      const attentionTxIds = attentionTransactions.map(tx => tx.id.uuid);
      const unreadMessageNotifications = hasMessageNotificationPayload
        ? payload.unreadMessageNotifications || []
        : [];
      const unreadMessageTxIds = hasMessageNotificationPayload
        ? getUnreadMessageNotificationTxIds(unreadMessageNotifications)
        : state.currentUserUnreadMessageTxIds;

      return {
        ...state,
        currentUserAttentionTransactions: attentionTransactions,
        currentUserAttentionTxIds: attentionTxIds,
        currentUserMessageNotifications: hasMessageNotificationPayload
          ? createNormalizedMessageNotificationMap({
              notifications: payload.messageNotifications || [],
              unreadMessageNotifications,
            })
          : state.currentUserMessageNotifications,
        currentUserUnreadMessageTxIds: unreadMessageTxIds,
        currentUserNotificationCount: new Set([...attentionTxIds, ...unreadMessageTxIds]).size,
      };
    }
    case FETCH_CURRENT_USER_NOTIFICATIONS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserNotificationCountError: payload };

    case FETCH_CURRENT_USER_HAS_ORDERS_REQUEST:
      return { ...state, currentUserHasOrdersError: null };
    case FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS:
      return { ...state, currentUserHasOrders: payload.hasOrders };
    case FETCH_CURRENT_USER_HAS_ORDERS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasOrdersError: payload };

    case SEND_VERIFICATION_EMAIL_REQUEST:
      return {
        ...state,
        sendVerificationEmailInProgress: true,
        sendVerificationEmailError: null,
      };
    case SEND_VERIFICATION_EMAIL_SUCCESS:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
      };
    case SEND_VERIFICATION_EMAIL_ERROR:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
        sendVerificationEmailError: payload,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const hasCurrentUserErrors = state => {
  const { user } = state;
  return (
    user.currentUserShowError ||
    user.currentUserHasListingsError ||
    user.currentUserNotificationCountError ||
    user.currentUserHasOrdersError
  );
};

export const verificationSendingInProgress = state => {
  return state.user.sendVerificationEmailInProgress;
};

export const currentUserSelector = state => {
  return state.user.currentUser;
};

// ================ Action creators ================ //

export const currentUserShowRequest = () => ({ type: CURRENT_USER_SHOW_REQUEST });

export const currentUserShowSuccess = user => ({
  type: CURRENT_USER_SHOW_SUCCESS,
  payload: user,
});

export const currentUserShowError = e => ({
  type: CURRENT_USER_SHOW_ERROR,
  payload: e,
  error: true,
});

export const clearCurrentUser = () => ({ type: CLEAR_CURRENT_USER });

const fetchCurrentUserHasListingsRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST,
});

export const fetchCurrentUserHasListingsSuccess = hasListings => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS,
  payload: { hasListings },
});

const fetchCurrentUserHasListingsError = e => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const fetchCurrentUserNotificationsRequest = () => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST,
});

export const fetchCurrentUserNotificationsSuccess = args => {
  const payload = {
    attentionTransactions: args?.attentionTransactions || [],
  };

  if (Object.prototype.hasOwnProperty.call(args || {}, 'messageNotifications')) {
    payload.messageNotifications = args?.messageNotifications;
  }

  if (Object.prototype.hasOwnProperty.call(args || {}, 'unreadMessageNotifications')) {
    payload.unreadMessageNotifications = args?.unreadMessageNotifications;
  }

  return {
    type: FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS,
    payload,
  };
};

const fetchCurrentUserNotificationsError = e => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserHasOrdersRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_REQUEST,
});

export const fetchCurrentUserHasOrdersSuccess = hasOrders => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS,
  payload: { hasOrders },
});

const fetchCurrentUserHasOrdersError = e => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_ERROR,
  error: true,
  payload: e,
});

export const sendVerificationEmailRequest = () => ({
  type: SEND_VERIFICATION_EMAIL_REQUEST,
});

export const sendVerificationEmailSuccess = () => ({
  type: SEND_VERIFICATION_EMAIL_SUCCESS,
});

export const sendVerificationEmailError = e => ({
  type: SEND_VERIFICATION_EMAIL_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const fetchCurrentUserHasListings = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasListingsRequest());
  const { currentUser } = getState().user;

  if (!currentUser) {
    dispatch(fetchCurrentUserHasListingsSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    // Since we are only interested in if the user has published
    // listings, we only need at most one result.
    states: 'published',
    page: 1,
    perPage: 1,
  };

  return sdk.ownListings
    .query(params)
    .then(response => {
      const hasListings = response.data.data && response.data.data.length > 0;

      const hasPublishedListings =
        hasListings &&
        ensureOwnListing(response.data.data[0]).attributes.state !== LISTING_STATE_DRAFT;
      dispatch(fetchCurrentUserHasListingsSuccess(!!hasPublishedListings));
    })
    .catch(e => dispatch(fetchCurrentUserHasListingsError(storableError(e))));
};

export const fetchCurrentUserHasOrders = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasOrdersRequest());

  if (!getState().user.currentUser) {
    dispatch(fetchCurrentUserHasOrdersSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    only: 'order',
    page: 1,
    perPage: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      dispatch(fetchCurrentUserHasOrdersSuccess(!!hasOrders));
    })
    .catch(e => dispatch(fetchCurrentUserHasOrdersError(storableError(e))));
};

// Notificaiton page size is max (100 items on page)
const NOTIFICATION_PAGE_SIZE = 100;

const dedupeAttentionTransactions = transactions => {
  return transactions.reduce((acc, transaction) => {
    const txId = transaction?.id?.uuid;

    if (!txId || acc.some(existingTx => existingTx?.id?.uuid === txId)) {
      return acc;
    }

    return [...acc, transaction];
  }, []);
};

export const fetchCurrentUserNotifications = (options = {}) => (dispatch, getState, sdk) => {
  const { includeMessageNotifications = true } = options;
  const requestMode = includeMessageNotifications
    ? FETCH_CURRENT_USER_NOTIFICATIONS_MODE.WITH_MESSAGES
    : FETCH_CURRENT_USER_NOTIFICATIONS_MODE.ATTENTION_ONLY;
  const sessionEpoch = userSessionEpoch;

  if (fetchCurrentUserNotificationsRequestPromise) {
    const canReuseInFlightRequest =
      fetchCurrentUserNotificationsRequestMode ===
        FETCH_CURRENT_USER_NOTIFICATIONS_MODE.WITH_MESSAGES ||
      fetchCurrentUserNotificationsRequestMode === requestMode;

    if (canReuseInFlightRequest) {
      return fetchCurrentUserNotificationsRequestPromise;
    }
  }

  const transitionsNeedingProviderAttention = getTransitionsNeedingProviderAttention();
  const transitionsNeedingCustomerAttention = getTransitionsNeedingCustomerAttention();
  dispatch(fetchCurrentUserNotificationsRequest());

  const providerAttentionPromise =
    transitionsNeedingProviderAttention.length > 0
      ? sdk.transactions.query({
          only: 'sale',
          last_transitions: transitionsNeedingProviderAttention,
          page: 1,
          perPage: NOTIFICATION_PAGE_SIZE,
        })
      : Promise.resolve({ data: { data: [] } });

  const customerAttentionPromise =
    transitionsNeedingCustomerAttention.length > 0
      ? sdk.transactions.query({
          only: 'order',
          last_transitions: transitionsNeedingCustomerAttention,
          page: 1,
          perPage: NOTIFICATION_PAGE_SIZE,
        })
      : Promise.resolve({ data: { data: [] } });

  const messagePromise = includeMessageNotifications
    ? getStreamChatNotificationsApi()
    : Promise.resolve(null);

  const request = Promise.allSettled([
    providerAttentionPromise,
    customerAttentionPromise,
    messagePromise,
  ])
    .then(results => {
      if (sessionEpoch !== userSessionEpoch) {
        return null;
      }

      const [providerAttentionResult, customerAttentionResult, messageResult] = results;

      const attentionTransactions = dedupeAttentionTransactions([
        ...(providerAttentionResult.status === 'fulfilled'
          ? providerAttentionResult.value?.data?.data || []
          : []),
        ...(customerAttentionResult.status === 'fulfilled'
          ? customerAttentionResult.value?.data?.data || []
          : []),
      ]);
      const messagePayload =
        includeMessageNotifications && messageResult.status === 'fulfilled'
          ? messageResult.value?.data || messageResult.value || {}
          : {};

      const messageNotifications = includeMessageNotifications
        ? messagePayload.notifications || []
        : undefined;
      const unreadMessageNotifications = includeMessageNotifications
        ? messagePayload.unreadNotifications || []
        : undefined;

      if (
        providerAttentionResult.status === 'rejected' &&
        customerAttentionResult.status === 'rejected' &&
        (!includeMessageNotifications || messageResult.status === 'rejected')
      ) {
        const error =
          providerAttentionResult.reason || customerAttentionResult.reason || messageResult.reason;
        dispatch(fetchCurrentUserNotificationsError(storableError(error)));
        throw error;
      }

      const successPayload = { attentionTransactions };

      if (includeMessageNotifications) {
        successPayload.messageNotifications = messageNotifications;
        successPayload.unreadMessageNotifications = unreadMessageNotifications;
      }

      dispatch(fetchCurrentUserNotificationsSuccess(successPayload));

      if (providerAttentionResult.status === 'rejected') {
        console.error(providerAttentionResult.reason); // eslint-disable-line
      }

      if (customerAttentionResult.status === 'rejected') {
        console.error(customerAttentionResult.reason); // eslint-disable-line
      }

      if (includeMessageNotifications && messageResult.status === 'rejected') {
        console.error(messageResult.reason); // eslint-disable-line
      }

      return {
        attentionTransactions,
        messageNotifications,
        unreadMessageNotifications,
      };
    })
    .finally(() => {
      if (fetchCurrentUserNotificationsRequestPromise === request) {
        fetchCurrentUserNotificationsRequestPromise = null;
        fetchCurrentUserNotificationsRequestMode = null;
      }
    });

  fetchCurrentUserNotificationsRequestPromise = request;
  fetchCurrentUserNotificationsRequestMode = requestMode;

  return request;
};

/**
 * Fetch currentUser API entity.
 *
 * @param {Object} options
 * @param {Object} [options.callParams]           Optional parameters for the currentUser.show().
 * @param {boolean} [options.updateHasListings]   Make extra call for fetchCurrentUserHasListings()?
 * @param {boolean} [options.updateNotifications] Make extra call for fetchCurrentUserNotifications()?
 * @param {boolean} [options.afterLogin]          Fetch is no-op for unauthenticated users except after login() call
 * @param {boolean} [options.enforce]             Enforce the call even if the currentUser entity is freshly fetched.
 */
export const fetchCurrentUser = options => (dispatch, getState, sdk) => {
  const state = getState();
  const { currentUserHasListings, currentUserShowTimestamp } = state.user || {};
  const { isAuthenticated } = state.auth;
  const sessionEpoch = userSessionEpoch;
  const {
    callParams = null,
    updateHasListings = true,
    updateNotifications = false,
    afterLogin,
    enforce = false, // Automatic emailVerification might be called too fast
  } = options || {};

  // Double fetch might happen when e.g. profile page is making a full page load
  const aSecondAgo = new Date().getTime() - 1000;
  if (!enforce && currentUserShowTimestamp > aSecondAgo) {
    return Promise.resolve({});
  }
  // Set in-progress, no errors
  dispatch(currentUserShowRequest());

  if (!isAuthenticated && !afterLogin) {
    // Make sure current user is null
    if (sessionEpoch === userSessionEpoch) {
      dispatch(currentUserShowSuccess(null));
    }
    return Promise.resolve({});
  }

  const parameters = callParams || {
    include: ['effectivePermissionSet', 'profileImage', 'stripeAccount'],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-xsmall',
      'variants.square-xsmall2x',
    ],
    'imageVariant.square-xsmall': sdkUtil.objectQueryString({
      w: 40,
      h: 40,
      fit: 'crop',
    }),
    'imageVariant.square-xsmall2x': sdkUtil.objectQueryString({
      w: 80,
      h: 80,
      fit: 'crop',
    }),
  };

  return sdk.currentUser
    .show(parameters)
    .then(response => {
      if (sessionEpoch !== userSessionEpoch) {
        return null;
      }

      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.show response');
      }
      const currentUser = entities[0];

      // Save stripeAccount to store.stripe.stripeAccount if it exists
      if (currentUser.stripeAccount) {
        dispatch(stripeAccountCreateSuccess(currentUser.stripeAccount));
      }
      // dispatch(retrieveOmiseRecipient());
      // set current user id to the logger
      log.setUserId(currentUser.id.uuid);
      dispatch(currentUserShowSuccess(currentUser));
      return persistResolvedActiveRoleIfNeeded(currentUser, sdk, dispatch);
    })
    .then(currentUser => {
      if (!currentUser || sessionEpoch !== userSessionEpoch) {
        return currentUser;
      }

      // If currentUser is not active (e.g. in 'pending-approval' state),
      // then they don't have listings or transactions that we care about.
      if (isUserAuthorized(currentUser)) {
        if (currentUserHasListings === false && updateHasListings !== false) {
          dispatch(fetchCurrentUserHasListings());
        }

        if (updateNotifications !== false) {
          dispatch(fetchCurrentUserNotifications());
        }

        if (!currentUser.attributes.emailVerified) {
          dispatch(fetchCurrentUserHasOrders());
        }
      }

      // Make sure auth info is up to date
      dispatch(authInfo());
    })
    .catch(e => {
      if (sessionEpoch !== userSessionEpoch) {
        return null;
      }

      // Make sure auth info is up to date
      dispatch(authInfo());
      log.error(e, 'fetch-current-user-failed');
      dispatch(currentUserShowError(storableError(e)));
    });
};

export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  if (verificationSendingInProgress(getState())) {
    return Promise.reject(new Error('Verification email sending already in progress'));
  }
  dispatch(sendVerificationEmailRequest());
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => dispatch(sendVerificationEmailSuccess()))
    .catch(e => dispatch(sendVerificationEmailError(storableError(e))));
};
